import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Textarea } from "ui";

import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";

import { useDebouncedInput, parseNullableString } from "./useDebouncedInput";

interface InstructionsFieldProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

export function InstructionsField(props: InstructionsFieldProps) {
  const { t } = useTranslation();
  const permissions = useWorkoutPermissions();
  const [isAdded, setIsAdded] = useState(!!props.value);
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { localValue, handleChange } = useDebouncedInput({
    initialValue: props.value ?? "",
    onChange: props.onChange,
    transform: parseNullableString,
  });

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing]);

  if (!isAdded) {
    if (!permissions.canEditItems) {
      return null;
    }

    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-slate-400 hover:text-slate-600 justify-start"
        onClick={() => {
          setIsAdded(true);
          setIsEditing(true);
        }}
      >
        + {t("editWorkout.addInstructions")}
      </Button>
    );
  }

  if (!isEditing || !permissions.canEditItems) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-[12px] font-medium text-slate-500">
          {t("editWorkout.instructions")}
        </label>
        <div
          className={cn(
            "text-sm text-slate-700 border border-slate-200 rounded-md px-3 py-2 line-clamp-4 min-h-[40px] whitespace-pre-wrap",
            {
              "cursor-pointer hover:bg-white/80 hover:border-slate-300 transition-colors":
                permissions.canEditItems,
            },
          )}
          onClick={() => {
            if (permissions.canEditItems) {
              setIsEditing(true);
            }
          }}
        >
          {localValue || (
            <span className="text-slate-400 italic">
              {t("editWorkout.instructionsPlaceholder")}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-medium text-slate-500">
        {t("editWorkout.instructions")}
      </label>
      <Textarea
        ref={textareaRef}
        value={localValue}
        onChange={handleChange}
        onBlur={() => {
          setIsEditing(false);
        }}
        placeholder={t("editWorkout.instructionsPlaceholder")}
        className="min-h-[60px] max-h-[240px] text-sm bg-white/80 border border-slate-200 resize-none overflow-y-auto"
        rows={4}
      />
    </div>
  );
}
